import React, { useRef } from "react";
// UI components imported as needed
import {
  loadProgrammeWithUID,
  loadUserSubInfoForProgram,
  checkProgrammingAccess,
  getBoxMember,
  isProgrammeReadDenied,
  firebaseAuth,
} from "@lemonadetraining/web-shared";

/* store */
import { appReduxStore } from "@lemonadetraining/web-shared";

/* Routing */
import { useParams, useLoaderData } from "react-router-dom";

/* components */
import MyCard from "../components/Cards/MyCard";
import WorkoutProgram from "../components/WorkoutProgram";
import PrivateProgrammePage from "./PrivateProgrammePage";
import ProgramPage from "../components/program/ProgramPage";
import {
  isProgrammeCreator,
  resolveProgrammeManageLane,
  canManageProgramme,
} from "../Common/programmeAccess";
import { readFeatureFlagsOrNull } from "../hooks/useFeatureFlags";
import { isGymProgrammeEditRightsEnabled } from "@lemonadetraining/contracts";

/**
 * Staff of the box that owns a programming get access without holding a
 * customer entitlement. Staff-ness mirrors the Firestore rules helpers:
 * the user's box must be the owning box, and their role must be admin or
 * coach — via `boxRole`, or the legacy `boxCoach` + `coachType: "box"`
 * claim pair. Deliberately CLAIMS-ONLY: the content reads behind this gate
 * (workout cards) are authorized by rules that read the token claims, so
 * admitting anyone the claims can't back would render an empty page —
 * a user whose claims are stale fails closed to the honest access card.
 */
const resolveBoxStaffRole = (
  claims: any,
  programmeBoxID: string | null | undefined
): "admin" | "coach" | null => {
  if (!programmeBoxID || !claims || claims.boxID !== programmeBoxID) {
    return null;
  }
  if (claims.boxRole === "admin") {
    return "admin";
  }
  if (claims.boxRole === "coach" || (claims.boxCoach === true && claims.coachType === "box")) {
    return "coach";
  }
  return null;
};

/*
 * Loaders run before AuthGuard: on a fresh tab neither the restored session nor
 * the claims have reached Redux yet, so wait for Firebase Auth and read the
 * token's claims when Redux has none.
 */
const readViewer = async (): Promise<{ uid: string | undefined; claims: any }> => {
  const auth = firebaseAuth as any;
  if (typeof auth?.authStateReady === "function") await auth.authStateReady();
  const current = auth?.currentUser ?? null;
  const userState = (appReduxStore.getState() as any).user;
  const uid: string | undefined = current?.uid ?? userState?.user?.uid;
  let claims = userState?.claims;
  if ((!claims || Object.keys(claims).length === 0) && current?.getIdTokenResult) {
    claims = (await current.getIdTokenResult()).claims;
  }
  return { uid, claims: claims || {} };
};

/* ============= [Loader] ====================== */
export async function loader({ params, request }: { params: { UID?: string }; request?: Request }) {
  const { UID } = params;
  const programUID = UID;
  const viewer = await readViewer();
  const userUID = viewer.uid;

  let programData;
  try {
    programData = await loadProgrammeWithUID(programUID);
  } catch (err) {
    // Only a signed-in viewer's denial means "private"; before the cross-domain
    // sign-in lands, the rules deny every read.
    const signInPending = request ? new URL(request.url).searchParams.has("authToken") : false;
    if (!isProgrammeReadDenied(err) || !firebaseAuth.currentUser || signInPending) throw err;
    return { denied: "private" as const };
  }
  if (!userUID || !programData) {
    return null;
  }

  /* check if isItMyProgramme */
  const isItMyProgramme = isProgrammeCreator(userUID, programData.data);

  /*
   * Access lanes, in order: creator/owner → box staff → entitled customer.
   * All staff of the owning box may OPEN the programming; canManage (the
   * edit UI) mirrors what the write callables accept: the creator, an admin
   * or owner of the owning box, or a box coach holding the box-granted
   * canCreatePrograms permission. Hard-deleting a card stays creator-only
   * (isCreator), matching the Firestore delete rule. Staff viewers carry no
   * subscription record, so their view has no subscription-derived calendar
   * floor — the card read rules allow box staff the full history.
   */
  const claims = viewer.claims;
  const staffRole = isItMyProgramme ? null : resolveBoxStaffRole(claims, programData.data.boxID);

  let canManage = isItMyProgramme;
  let staffViewOnly = false;
  let userSubInfo: any = null;

  if (staffRole) {
    const lane = resolveProgrammeManageLane(claims, userUID, programData.data);
    // An unreadable flags document keeps today's rights; the write callables still enforce.
    const [member, flags] = await Promise.all([
      lane === "coach"
        ? getBoxMember(programData.data.boxID, userUID).catch(() => null)
        : Promise.resolve(null),
      lane === "admin" ? Promise.resolve(null) : readFeatureFlagsOrNull(),
    ]);
    const coachHasGrant = member?.permissions?.canCreatePrograms === true;
    const editRightsEnforced = isGymProgrammeEditRightsEnabled(flags);
    canManage = canManageProgramme(lane, coachHasGrant, editRightsEnforced);
    staffViewOnly = editRightsEnforced && !canManage;
  } else if (!isItMyProgramme) {
    // Customer entitlement lanes — the paywall. Unchanged by the staff lane.
    if (programData.data.boxID) {
      // For box programmings, verify user has active membership
      const hasAccess = await checkProgrammingAccess(programUID);
      if (!hasAccess) {
        return null;
      }
      userSubInfo = await loadUserSubInfoForProgram(userUID, programUID);
    } else {
      userSubInfo = await loadUserSubInfoForProgram(userUID, programUID);
      if (!userSubInfo) {
        return null;
      }
    }
  }

  return {
    programData: { ...programData.data, programmingUID: programData.id },
    userSubInfo,
    canManage,
    staffViewOnly,
    isCreator: isItMyProgramme,
    isStaff: Boolean(staffRole),
  };
}

/* =================== ViewWorkoutProgramme ==================== */
const ViewWorkoutProgramme: React.FC = () => {
  const params = useParams();
  const programmingRef = useRef<any>(null);
  const canManage = useRef(false);
  const isCreator = useRef(false);
  const staffViewOnly = useRef(false);
  const runsIt = useRef(false);
  const userSubInfo = useRef<any>(null);

  /* Load the programming */
  const loaderData = useLoaderData() as any;
  const isPrivate = loaderData?.denied === "private";
  if (loaderData && !isPrivate) {
    programmingRef.current = loaderData.programData;
    userSubInfo.current = loaderData.userSubInfo;
    canManage.current = loaderData.canManage;
    isCreator.current = loaderData.isCreator;
    staffViewOnly.current = loaderData.staffViewOnly === true;
    runsIt.current = loaderData.canManage === true || loaderData.isStaff === true;
  }

  // F032 step 7b: the people who run a program get the planner; members keep their view.
  if (!isPrivate && programmingRef.current && runsIt.current) {
    return (
      <div className="flex w-full flex-1 flex-col">
        <ProgramPage
          key={params.UID}
          program={{ ...programmingRef.current, programmingUID: params.UID || "" }}
          canManage={canManage.current}
          isCreator={isCreator.current}
        />
      </div>
    );
  }

  /* -------- Return ----------- */
  return (
    <div className="flex w-full flex-1 flex-col">
      <div className="mx-auto flex flex-col gap-3 px-2 py-2 md:px-6 md:py-3">
        {isPrivate && <PrivateProgrammePage key={params.UID} programmeID={params.UID ?? ""} />}
        {!isPrivate && !programmingRef.current && (
          <MyCard>
            <div>
              <h3 className="text-base font-medium">Something went wrong</h3>
              <p className="text-muted-foreground text-sm">
                We couldn&apos;t open this programme. It may have been removed, or you may not have
                access. If this keeps happening, contact us.
              </p>
            </div>
          </MyCard>
        )}
        {!isPrivate && programmingRef.current && (
          <WorkoutProgram
            viewOnly={!canManage.current}
            isItMyProgramme={canManage.current}
            isCreator={isCreator.current}
            staffViewOnly={staffViewOnly.current}
            programData={programmingRef.current}
            programmingUID={params.UID || ""}
            subInfo={userSubInfo.current}
          />
        )}
      </div>
    </div>
  );
};

export default ViewWorkoutProgramme;
